import { formatDistanceToNow } from 'date-fns'
import React from 'react'
import {
  ContributionImportProgress,
  GetLatestJobResponse,
  JobTracking,
} from '../types/types'
import ProcessingStatusCard, { WorkflowStep } from './ProcessingStatusCard'
import { jobIsActive } from '../hooks/useContributionsJobs'
import { convertToLocalFromUTCISODate } from 'helpers/datetime'

interface ContributionProcessingSectionProps {
  jobResponse: GetLatestJobResponse
  modelLabel: string
  benchmarkLabel: string
}

function getTimeSinceLastUpdate(jobTracking: JobTracking) {
  if (!jobTracking.metadata?.lastUpdatedAt) return 'N/A'
  return formatDistanceToNow(new Date(jobTracking.metadata?.lastUpdatedAt), {
    addSuffix: true,
  })
}

function getImportLastUpdated(importProgress: ContributionImportProgress) {
  if (!importProgress.lastUpdatedAt) return 'N/A'
  return formatDistanceToNow(new Date(importProgress.lastUpdatedAt), {
    addSuffix: true,
  })
}

const STAGING_DONE_PHASES = ['ready', 'finalize', 'draining', 'completed']
const SAVING_ACTIVE_PHASES = ['ready', 'finalize', 'draining']

/**
 * Map the backend import phases onto a two-step workflow the operator can read:
 *   1. Staging rules   — fan-out writes rules into the staging table
 *   2. Saving to database — drain staging into ClientBenefitContribution
 * Each step exposes its own counts + progress; there is no single conflated bar.
 */
function buildImportSteps(
  p: ContributionImportProgress,
  jobActive: boolean
): WorkflowStep[] {
  // status and phase both derive from the same backend phase, but treat either
  // reporting "failed" as failed so the card can't render active/non-failed.
  const failed = p.status === 'failed' || p.phase === 'failed'

  // Step 1: Staging
  let stagingState: WorkflowStep['state']
  if (failed) {
    stagingState = p.rowsWritten > 0 ? 'done' : 'error'
  } else if (STAGING_DONE_PHASES.includes(p.phase)) {
    stagingState = 'done'
  } else {
    stagingState = 'active'
  }
  const stagingDetail =
    p.chunksTotal != null
      ? `${p.rowsWritten.toLocaleString()} staged · ${p.chunksDone}/${p.chunksTotal} groups`
      : `${p.rowsWritten.toLocaleString()} rows staged`
  const stagingPercent =
    stagingState === 'active' && p.chunksTotal
      ? Math.round((p.chunksDone / p.chunksTotal) * 100)
      : undefined

  // Step 2: Saving. rowsDrained (popped) can exceed rowsWritten when chunk
  // retries left duplicate staging rows the drain later dedupes; cap the count.
  const saved =
    p.rowsWritten > 0 ? Math.min(p.rowsDrained, p.rowsWritten) : p.rowsDrained
  let savingState: WorkflowStep['state']
  if (p.phase === 'completed') {
    // The import phase is done, but the underlying job may still be applying/
    // activating. Keep "Saving" active until the job itself goes idle so the
    // card never shows fully complete while work is still in progress.
    savingState = jobActive ? 'active' : 'done'
  } else if (failed) {
    savingState = p.rowsWritten > 0 ? 'error' : 'pending'
  } else if (SAVING_ACTIVE_PHASES.includes(p.phase)) {
    savingState = 'active'
  } else {
    savingState = 'pending'
  }

  let savingDetail: string
  let savingPercent: number | undefined
  if (p.phase === 'ready' || p.phase === 'finalize') {
    // Drain has not started yet — show the sub-status, no progress.
    savingDetail = p.phaseLabel
    savingPercent = undefined
  } else {
    savingDetail =
      p.rowsWritten > 0
        ? `${saved.toLocaleString()} / ${p.rowsWritten.toLocaleString()} rows saved`
        : `${p.rowsDrained.toLocaleString()} rows saved`
    savingPercent =
      savingState === 'active' && p.rowsWritten > 0
        ? Math.round((saved / p.rowsWritten) * 100)
        : undefined
  }

  return [
    {
      key: 'staging',
      title: 'Staging rules',
      state: stagingState,
      detail: stagingDetail,
      percent: stagingPercent,
    },
    {
      key: 'saving',
      title: 'Saving to database',
      state: savingState,
      detail: savingDetail,
      percent: savingPercent,
    },
  ]
}

export default function ContributionProcessingSection({
  jobResponse,
  modelLabel,
  benchmarkLabel,
}: ContributionProcessingSectionProps) {
  const { job, jobTracking, import: importProgress } = jobResponse
  const { metadata } = jobTracking

  if (importProgress) {
    // Polling stays active while the underlying job runs even after the import
    // phase reports completed. Don't present the card as completed until the
    // job is also idle, otherwise it shows finished while apply is in progress.
    const jobActive = jobIsActive(job)
    const failed =
      importProgress.status === 'failed' || importProgress.phase === 'failed'
    const displayStatus = failed
      ? 'failed'
      : importProgress.phase === 'completed' && jobActive
        ? 'processing'
        : importProgress.status

    return (
      <ProcessingStatusCard
        status={displayStatus}
        steps={buildImportSteps(importProgress, jobActive)}
        modelLabel={modelLabel ?? 'N/A'}
        benchmarkLabel={benchmarkLabel ?? 'N/A'}
        startedOn={
          importProgress.startedOn
            ? convertToLocalFromUTCISODate(importProgress.startedOn)
            : job.startedOn
              ? convertToLocalFromUTCISODate(job.startedOn)
              : 'N/A'
        }
        lastUpdated={getImportLastUpdated(importProgress)}
        error={importProgress.error?.message}
      />
    )
  }

  // Simple (non-large-model) apply: a single direct phase, no staging/drain.
  const simpleStatus = metadata.status
  const simpleSteps: WorkflowStep[] = [
    {
      key: 'applying',
      title: 'Applying rules',
      state:
        simpleStatus === 'completed'
          ? 'done'
          : simpleStatus === 'failed'
            ? 'error'
            : 'active',
      detail: `${(metadata.rulesProcessed ?? 0).toLocaleString()} rules processed`,
    },
  ]

  return (
    <ProcessingStatusCard
      status={simpleStatus}
      steps={simpleSteps}
      modelLabel={modelLabel ?? 'N/A'}
      benchmarkLabel={benchmarkLabel ?? 'N/A'}
      startedOn={
        job.startedOn ? convertToLocalFromUTCISODate(job.startedOn) : 'N/A'
      }
      lastUpdated={getTimeSinceLastUpdate(jobTracking)}
    />
  )
}
